<template>
  <main class="software-page">
    <!-- Per-page SEO: unique title + meta description (Requirement 10.1). -->
    <SeoHead :seo="content.seo" />

    <!--
      Civillo product hero. This page renders whenever /software is reached
      directly, regardless of the Software feature flag — the flag only gates
      the navigation link, not the route (Requirements 2.1, 2.2, 2.4).
    -->
    <section class="software-hero" aria-labelledby="software-heading">
      <v-container>
        <ScrollReveal>
          <div class="software-hero__inner">
            <img
              :src="softwareIcon"
              alt=""
              aria-hidden="true"
              class="software-hero__icon"
            />
            <p class="software-hero__eyebrow">{{ content.hero.eyebrow }}</p>
            <h1 id="software-heading" class="software-heading">
              {{ content.hero.name }}
            </h1>
            <p class="software-lead">{{ content.hero.lead }}</p>
            <p class="software-body">{{ content.hero.body }}</p>
            <CtaButton
              :to="content.closingCta.cta.to"
              :label="content.closingCta.cta.label"
            />
          </div>
        </ScrollReveal>
      </v-container>
    </section>

    <!-- Traction figures -->
    <section class="software-stats" aria-label="Civillo usage">
      <v-container>
        <div class="software-stats__grid">
          <div
            v-for="stat in content.stats"
            :key="stat.label"
            class="software-stat"
          >
            <span class="software-stat__value">{{ stat.value }}</span>
            <span class="software-stat__label">{{ stat.label }}</span>
          </div>
        </div>
      </v-container>
    </section>

    <!-- The problem Civillo addresses -->
    <section class="software-section" aria-labelledby="software-problem-heading">
      <v-container>
        <ScrollReveal>
          <div class="software-section__inner">
            <h2 id="software-problem-heading" class="software-section__heading">
              {{ content.problem.heading }}
            </h2>
            <p
              v-for="(paragraph, index) in content.problem.body"
              :key="index"
              class="software-section__body"
            >{{ paragraph }}</p>
          </div>
        </ScrollReveal>
      </v-container>
    </section>

    <!-- What the platform aggregates -->
    <section
      class="software-section software-section--alt"
      aria-labelledby="software-features-heading"
    >
      <v-container>
        <ScrollReveal>
          <h2 id="software-features-heading" class="software-section__heading">
            {{ content.features.heading }}
          </h2>
          <div class="software-features__grid">
            <div
              v-for="item in content.features.items"
              :key="item.title"
              class="software-feature"
            >
              <h3 class="software-feature__title">{{ item.title }}</h3>
              <p class="software-feature__body">{{ item.body }}</p>
            </div>
          </div>
        </ScrollReveal>
      </v-container>
    </section>

    <!-- Permit module -->
    <section class="software-section" aria-labelledby="software-permits-heading">
      <v-container>
        <ScrollReveal>
          <div class="software-section__inner">
            <h2 id="software-permits-heading" class="software-section__heading">
              {{ content.permits.heading }}
            </h2>
            <p
              v-for="(paragraph, index) in content.permits.body"
              :key="index"
              class="software-section__body"
            >{{ paragraph }}</p>
          </div>
        </ScrollReveal>
      </v-container>
    </section>

    <!-- Closing CTA -->
    <section class="software-cta" aria-labelledby="software-cta-heading">
      <v-container>
        <ScrollReveal>
          <div class="software-section__inner">
            <h2 id="software-cta-heading" class="software-cta__heading">
              {{ content.closingCta.heading }}
            </h2>
            <p class="software-section__body">{{ content.closingCta.body }}</p>
            <CtaButton
              :to="content.closingCta.cta.to"
              :label="content.closingCta.cta.label"
            />
          </div>
        </ScrollReveal>
      </v-container>
    </section>
  </main>
</template>

<script>
// SoftwarePage — presents Civillo, ThinkSpatial's web-mapping platform for
// construction projects (Requirement 2).
//
// The page always renders when /software is reached directly, independent of
// the Software feature flag; the flag only controls whether the menu item
// appears in the Navigation_Bar (Requirements 2.1, 2.2).
//
// Content comes from the structured module `client/src/content/software.js`,
// mirroring the ThinkSpatial Capability Statement.
//
// Uses the Options API to match the project's `plugin:vue/essential` ESLint
// preset. The brand Software icon is imported as a URL asset and rendered via
// <img>, the Vue 3-compatible SVG mechanism used across the redesign
// (Requirement 8.3).
import software from '@/content/software'
import SeoHead from '@/components/SeoHead.vue'
import ScrollReveal from '@/components/ScrollReveal.vue'
import CtaButton from '@/components/CtaButton.vue'
import softwareIcon from 'assets/svg/icon_software.svg'

export default {
  name: 'SoftwarePage',
  components: {
    SeoHead,
    ScrollReveal,
    CtaButton,
  },
  data () {
    return {
      // Brand Software icon (decorative; alt is empty + aria-hidden).
      softwareIcon,
      content: software,
    }
  },
}
</script>

<style lang="scss" scoped>
.software-hero {
  padding: 80px 0 48px;
  background-color: #f7f5f4;
}

.software-hero__inner {
  max-width: 760px;
  margin: 0 auto;
  text-align: center;
}

.software-hero__icon {
  width: 72px;
  height: 72px;
  margin-bottom: 20px;
}

.software-hero__eyebrow {
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #e7511e;
  margin-bottom: 8px;
}

.software-heading {
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.1;
  color: #41281b;
  margin-bottom: 12px;
}

.software-lead {
  font-size: 1.25rem;
  font-weight: 600;
  color: #4a4a4a;
  margin-bottom: 16px;
}

.software-body {
  font-size: 1.05rem;
  color: #4a4a4a;
  line-height: 1.75;
  margin-bottom: 32px;
}

// --- Traction figures ---------------------------------------------------

.software-stats {
  padding: 40px 0;
}

.software-stats__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
  text-align: center;
}

.software-stat {
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  border-top: 4px solid #e7511e;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(65, 40, 27, 0.06);
}

.software-stat__value {
  font-size: 1.8rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 4px;
}

.software-stat__label {
  font-size: 0.98rem;
  color: #4a4a4a;
  line-height: 1.5;
}

// --- Content sections --------------------------------------------------

.software-section {
  padding: 48px 0;
}

.software-section--alt {
  background-color: #f7f5f4;
}

.software-section__inner {
  max-width: 760px;
}

.software-section__heading {
  font-size: 1.8rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 16px;
}

.software-section__body {
  font-size: 1.05rem;
  color: #4a4a4a;
  line-height: 1.75;
  margin-bottom: 16px;
}

.software-features__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-top: 8px;
}

.software-feature {
  background-color: #fff;
  border: 1px solid rgba(65, 40, 27, 0.1);
  border-radius: 12px;
  padding: 24px;
}

.software-feature__title {
  font-size: 1.1rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 8px;
}

.software-feature__body {
  font-size: 1rem;
  color: #4a4a4a;
  line-height: 1.6;
}

// --- Closing CTA -------------------------------------------------------

.software-cta {
  padding: 48px 0 80px;
  background-color: #f7f5f4;
}

.software-cta__heading {
  font-size: 1.8rem;
  font-weight: 800;
  color: #41281b;
  margin-bottom: 12px;
}
</style>
